import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Avatar,
  Stack,
  LinearProgress,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  OpenInNew as OpenInNewIcon,
  Phone as PhoneIcon,
  Message as MessageIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

import conversationService, {
  ConversationSummary,
  ConversationFilters,
  ConversationSortField,
  SortDirection,
} from '../../services/conversationService';
import { websocketService } from '../../services/websocketService';

interface ConversationListProps {
  filters?: ConversationFilters;
  searchQuery?: string;
  onConversationSelect: (conversationId: string) => void;
  onConversationOpenFullPage?: (conversationId: string) => void;
  onConversationOpenInNewTab?: (conversationId: string) => void;
  sortBy?: ConversationSortField;
  sortDirection?: SortDirection;
  onSortChange?: (sortBy: ConversationSortField, sortDirection: SortDirection) => void;
  refreshTrigger?: number;
}

const ConversationList: React.FC<ConversationListProps> = ({
  filters,
  searchQuery,
  onConversationSelect,
  onConversationOpenFullPage,
  onConversationOpenInNewTab,
  sortBy,
  sortDirection,
  onSortChange,
  refreshTrigger,
}) => {
  const openConversationFullPage = onConversationOpenFullPage ?? onConversationSelect;
  const openConversationInNewTab = onConversationOpenInNewTab ?? onConversationSelect;
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const loadConversations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const requestFilters: ConversationFilters = {
        ...filters,
        archive_status: filters?.archive_status ?? 'active',
        sort_by: sortBy,
        sort_direction: sortDirection,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      };

      let response;
      if (searchQuery && searchQuery.trim()) {
        response = await conversationService.searchConversations({
          query: searchQuery.trim(),
          filters: requestFilters,
        });
      } else {
        response = await conversationService.getConversations(requestFilters);
      }

      setConversations(response.conversations);
      setTotalCount(response.total_count);
    } catch (err) {
      console.error('Error loading conversations:', err);
      setError('Error al cargar las conversaciones. Por favor, intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  }, [filters, searchQuery, page, rowsPerPage, sortBy, sortDirection]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations, refreshTrigger]);

  useEffect(() => {
    setPage(0);
  }, [filters, searchQuery]);

  // Subscribe to WebSocket updates for real-time conversation updates
  useEffect(() => {
    const handleNewMessage = (event: any) => {
      if (event.type === 'message_updated') {
        void loadConversations();
        return;
      }

      if (event.type === 'new_message' && event.conversation) {
        const isDefaultLastActivitySort =
          (sortBy ?? 'last_activity_at') === 'last_activity_at' &&
          (sortDirection ?? 'desc') === 'desc';
        const shouldPromoteInPlace = isDefaultLastActivitySort && page === 0;

        if (!shouldPromoteInPlace) {
          void loadConversations();
          return;
        }

        setConversations((prevConversations) => {
          const existingIndex = prevConversations.findIndex(
            (conv) => conv.id === event.conversation_id
          );

          if (existingIndex === -1) {
            void loadConversations();
            return prevConversations;
          }

          const updatedConversation = {
            ...prevConversations[existingIndex],
            customer_name: event.conversation.customer_name ?? prevConversations[existingIndex].customer_name,
            customer_phone: event.conversation.customer_phone ?? prevConversations[existingIndex].customer_phone,
            message_count: event.conversation.message_count,
            lead_status: event.conversation.lead_status,
            ai_enabled: event.conversation.ai_enabled,
            delivery_status: event.conversation.delivery_status ?? prevConversations[existingIndex].delivery_status,
            last_activity_at: event.timestamp,
            last_message_preview: event.content ?? prevConversations[existingIndex].last_message_preview,
          };

          const newConversations = [...prevConversations];
          newConversations.splice(existingIndex, 1);
          return [updatedConversation, ...newConversations];
        });
      }
    };

    // Subscribe to conversations topic
    const unsubscribe = websocketService.subscribe('conversations', handleNewMessage);

    return () => {
      // Unsubscribe when component unmounts
      unsubscribe();
    };
  }, [loadConversations, page, sortBy, sortDirection]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (field: ConversationSortField) => {
    const nextDirection: SortDirection =
      sortBy === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setPage(0);
    onSortChange?.(field, nextDirection);
  };

  const handleConversationRowClick = (
    event: React.MouseEvent<HTMLTableRowElement>,
    conversationId: string
  ) => {
    if (event.ctrlKey || event.metaKey) {
      openConversationInNewTab(conversationId);
      return;
    }

    onConversationSelect(conversationId);
  };

  const formatPhoneNumber = (phone?: string): string => {
    if (!phone) return 'N/A';
    // Format Colombian phone numbers
    if (phone.startsWith('57') && phone.length === 12) {
      return `+${phone.slice(0, 2)} ${phone.slice(2, 5)} ${phone.slice(5, 8)} ${phone.slice(8)}`;
    }
    return phone;
  };

  const renderSortLabel = (label: string, field: ConversationSortField) => (
    <TableSortLabel
      active={sortBy === field}
      direction={sortBy === field ? sortDirection || 'asc' : 'asc'}
      onClick={() => handleSort(field)}
    >
      {label}
    </TableSortLabel>
  );

  if (loading && conversations.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      {loading && <LinearProgress />}
      
      <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{renderSortLabel('Cliente', 'customer')}</TableCell>
              <TableCell>{renderSortLabel('Estado', 'lead_status')}</TableCell>
              <TableCell>{renderSortLabel('Proyecto', 'project_interest')}</TableCell>
              <TableCell align="center">{renderSortLabel('Mensajes', 'message_count')}</TableCell>
              <TableCell>{renderSortLabel('Duración', 'duration_minutes')}</TableCell>
              <TableCell>{renderSortLabel('Última Actividad', 'last_activity_at')}</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {conversations.map((conversation) => (
              <TableRow
                key={conversation.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={(event) => handleConversationRowClick(event, conversation.id)}
              >
                <TableCell>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                      <PhoneIcon fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {conversation.customer_name || 'Cliente Anónimo'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatPhoneNumber(conversation.customer_phone)}
                      </Typography>
                    </Box>
                  </Stack>
                </TableCell>
                
                <TableCell>
                  <Chip
                    label={conversationService.formatLeadStatus(conversation.lead_status)}
                    size="small"
                    sx={{
                      backgroundColor: conversationService.getLeadStatusColor(conversation.lead_status),
                      color: 'white',
                      fontWeight: 'medium',
                    }}
                  />
                </TableCell>
                
                <TableCell>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                    {conversation.project_interest || 'No especificado'}
                  </Typography>
                </TableCell>
                
                <TableCell align="center">
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                    <MessageIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      {conversation.message_count}
                    </Typography>
                  </Stack>
                </TableCell>
                
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <ScheduleIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      {conversationService.formatDuration(conversation.duration_minutes)}
                    </Typography>
                  </Stack>
                </TableCell>
                
                <TableCell>
                  {conversation.last_activity_at ? (
                    <Typography variant="body2" color="text.secondary">
                      {formatDistanceToNow(parseISO(conversation.last_activity_at), {
                        addSuffix: true,
                        locale: es,
                      })}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Sin actividad
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" display="block">
                    {format(
                      parseISO(conversation.last_activity_at || conversation.start_time),
                      'dd/MM/yyyy HH:mm'
                    )}
                  </Typography>
                </TableCell>
                
                <TableCell align="center">
                  <Stack direction="row" spacing={0.5} justifyContent="center">
                    <Tooltip title="Ver detalles">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          openConversationFullPage(conversation.id);
                        }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Abrir en nueva pestaña">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          openConversationInNewTab(conversation.id);
                        }}
                      >
                        <OpenInNewIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      {conversations.length === 0 && !loading && (
        <Box p={4} textAlign="center">
          <Typography variant="body1" color="text.secondary">
            {searchQuery
              ? 'No se encontraron conversaciones que coincidan con tu búsqueda.'
              : 'No hay conversaciones disponibles.'}
          </Typography>
        </Box>
      )}
      
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Filas por página:"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
        }
      />
    </Paper>
  );
};

export default ConversationList;
